import { COLORS, HOME, HOUSE, SAFE, TRACK, pieceCell, targetOf } from "@/lib/ludoBoard";

const S = 40;
const ORDER = ["red", "green", "yellow", "blue"];
const DARK = { red: "#A3161B", green: "#16782F", yellow: "#C79A00", blue: "#0D57A8" };
const LIGHT = { red: "#FF7A7D", green: "#6EE08A", yellow: "#FFF07A", blue: "#6CB8FF" };
const ARROW = { 0: 0, 13: 90, 26: 180, 39: 270 };

const starPath = (cx, cy, R = 11, r = 4.6) => Array.from({ length: 10 }, (_, i) => {
  const a = (Math.PI / 5) * i - Math.PI / 2;
  const d = i % 2 ? r : R;
  return `${(cx + d * Math.cos(a)).toFixed(1)},${(cy + d * Math.sin(a)).toFixed(1)}`;
}).join(" ");

const Defs = () => (
  <defs>
    {ORDER.map((c) => (
      <g key={c}>
        <linearGradient id={`lb-${c}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={LIGHT[c]} /><stop offset="0.45" stopColor={COLORS[c]} /><stop offset="1" stopColor={DARK[c]} />
        </linearGradient>
        <radialGradient id={`lh-${c}`} cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#fff" stopOpacity="0.95" /><stop offset="0.25" stopColor={LIGHT[c]} /><stop offset="1" stopColor={DARK[c]} />
        </radialGradient>
      </g>
    ))}
    <filter id="lb-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="2" stdDeviation="1.6" floodColor="#000" floodOpacity="0.45" />
    </filter>
  </defs>
);

const House = ({ color, r, c }) => (
  <g>
    <rect x={c * S} y={r * S} width={6 * S} height={6 * S} fill={`url(#lb-${color})`} />
    <rect x={(c + 0.9) * S} y={(r + 0.9) * S} width={4.2 * S} height={4.2 * S} rx="16" fill="#fff" stroke={DARK[color]} strokeWidth="2" />
    {[[1.5, 1.5], [1.5, 3.5], [3.5, 1.5], [3.5, 3.5]].map(([dr, dc]) => (
      <circle key={`${dr}${dc}`} cx={(c + dc) * S} cy={(r + dr) * S} r="15" fill={COLORS[color]} stroke={DARK[color]} strokeWidth="2" opacity="0.9" />
    ))}
  </g>
);

const Cell = ({ r, c, fill, children }) => (
  <g>
    <rect x={c * S} y={r * S} width={S} height={S} fill={fill} stroke="#9AA3AF" strokeWidth="0.8" />
    {children}
  </g>
);

const Grid = () => (
  <g>
    <rect width={15 * S} height={15 * S} fill="#fff" />
    {Object.entries(HOUSE).map(([color, [r, c]]) => <House key={color} color={color} r={r} c={c} />)}
    {TRACK.map(([r, c], i) => {
      const start = i % 13 === 0;
      const col = start && ORDER[i / 13];
      return (
        <Cell key={i} r={r} c={c} fill={start ? COLORS[col] : "#fff"}>
          {start && <path d="M-7,-5 L5,0 L-7,5 Z" fill="#fff" transform={`translate(${(c + 0.5) * S},${(r + 0.5) * S}) rotate(${ARROW[i]})`} />}
          {SAFE.has(i) && !start && <polygon points={starPath((c + 0.5) * S, (r + 0.5) * S)} fill="none" stroke="#8B95A1" strokeWidth="1.6" strokeLinejoin="round" />}
        </Cell>
      );
    })}
    {Object.entries(HOME).map(([color, cells]) => cells.map(([r, c], i) => <Cell key={`${color}${i}`} r={r} c={c} fill={COLORS[color]} />))}
    <polygon points={`${6 * S},${6 * S} ${9 * S},${6 * S} ${7.5 * S},${7.5 * S}`} fill={`url(#lb-green)`} stroke="#fff" strokeWidth="1.5" />
    <polygon points={`${9 * S},${6 * S} ${9 * S},${9 * S} ${7.5 * S},${7.5 * S}`} fill={`url(#lb-yellow)`} stroke="#fff" strokeWidth="1.5" />
    <polygon points={`${9 * S},${9 * S} ${6 * S},${9 * S} ${7.5 * S},${7.5 * S}`} fill={`url(#lb-blue)`} stroke="#fff" strokeWidth="1.5" />
    <polygon points={`${6 * S},${9 * S} ${6 * S},${6 * S} ${7.5 * S},${7.5 * S}`} fill={`url(#lb-red)`} stroke="#fff" strokeWidth="1.5" />
    <rect width={15 * S} height={15 * S} fill="none" stroke="#4B5563" strokeWidth="3" />
  </g>
);

const Pawn = ({ color, me, done }) => (
  <g filter="url(#lb-shadow)" transform="scale(1.3)">
    <ellipse cy="11" rx="12" ry="4.5" fill="#000" opacity="0.25" />
    <path d="M-11,10 Q-11,4 -6,1 L-4,-3 L4,-3 L6,1 Q11,4 11,10 Q0,14 -11,10 Z" fill={`url(#lb-${color})`} stroke={me ? "#fff" : DARK[color]} strokeWidth={me ? 2 : 1.2} />
    <circle cy="-9" r="8" fill={`url(#lh-${color})`} stroke={me ? "#fff" : DARK[color]} strokeWidth={me ? 2 : 1.2} />
    <ellipse cx="-2.5" cy="-12" rx="2.6" ry="1.6" fill="#fff" opacity="0.8" />
    {done && <path d="M-4,-9 L-1,-6 L4,-12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" />}
  </g>
);

export const LudoBoard = ({ state, me, legal = [], die, onPick }) => {
  const stacks = {};
  const pieces = state.players.flatMap((pl) => (pl.status === "forfeit" ? [] : pl.pieces.map((p, i) => {
    const [r, c] = pieceCell(pl.color, p, i);
    const key = `${r}:${c}`;
    stacks[key] = (stacks[key] || 0) + 1;
    return { pl, p, i, r, c, k: stacks[key] - 1 };
  })));
  const mine = state.players.find((pl) => pl.id === me);
  const myTurn = state.players[state.turn]?.id === me && state.phase === "move";
  return (
    <svg viewBox={`0 0 ${15 * S} ${15 * S}`} className="h-auto w-full select-none rounded-xl" data-testid="ludo-board">
      <Defs />
      <Grid />
      {myTurn && mine && legal.map((i) => {
        const [r, c] = pieceCell(mine.color, targetOf(mine.pieces[i], die), i);
        return <circle key={`t${i}`} cx={c * S} cy={r * S} r="15" fill="none" stroke={DARK[mine.color]} strokeWidth="2.5" strokeDasharray="4 4" opacity="0.9" data-testid={`ludo-target-${i}`} />;
      })}
      {pieces.map(({ pl, p, i, r, c, k }) => {
        const playable = myTurn && pl.id === me && legal.includes(i);
        return (
          <g key={`${pl.id}-${i}`} style={{ transform: `translate(${c * S + k * 6}px, ${r * S - k * 6}px)`, transition: "transform 450ms cubic-bezier(.3,1.4,.5,1)" }}
            onClick={playable ? () => onPick(i) : undefined} className={playable ? "cursor-pointer" : ""}
            data-testid={pl.id === me ? `ludo-piece-me-${i}` : `ludo-piece-${pl.color}-${i}`}>
            {playable && <circle r="24" fill="transparent" data-testid={`ludo-hit-${i}`} />}
            {playable && <circle cy="2" r="18" fill="#fff" opacity="0.55" stroke={DARK[pl.color]} strokeWidth="2"><animate attributeName="r" values="14;20;14" dur="1s" repeatCount="indefinite" /></circle>}
            <g className={playable ? "ludo-hop" : ""}><Pawn color={pl.color} me={pl.id === me} done={p >= 56} /></g>
          </g>
        );
      })}
    </svg>
  );
};

export const LudoTable = ({ children }) => (
  <div className="ludo-table w-full max-w-[640px] rounded-[28px] p-3 sm:p-4" data-testid="ludo-table">
    <div className="rounded-2xl bg-[#5B3A1E] p-2 shadow-[inset_0_2px_6px_rgba(0,0,0,0.6)] sm:p-3">{children}</div>
  </div>
);
